import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'

export interface UseMapClickMarkerProps {
  mapClickCoordinates?: { latitude: number; longitude: number } | null
}

/**
 * Hook for managing map click marker rendering
 * Shows a red marker when user clicks on empty map areas
 */
export function useMapClickMarker({
  mapClickCoordinates
}: UseMapClickMarkerProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()

  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded) {
      return
    }

    if (!mapClickCoordinates) {
      // Clean up marker if coordinates are cleared
      // We already know styleLoaded is true (passed the check above)
      if (map.current) {
        try {
          if (map.current.getLayer('map-click-marker')) {
            map.current.removeLayer('map-click-marker')
          }
          if (map.current.getSource('map-click-marker')) {
            map.current.removeSource('map-click-marker')
          }
        } catch (error) {
        }
      }
      return
    }

    // Helper function to add marker when style is ready
    const addMarkerInternal = (): void => {
      if (!map.current || !mapClickCoordinates) return

      const clickFeature = {
        type: 'Feature' as const,
        properties: {
          id: 'map-click',
          type: 'click'
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [mapClickCoordinates.longitude, mapClickCoordinates.latitude]
        }
      }

      const geoJsonData = {
        type: 'FeatureCollection' as const,
        features: [clickFeature]
      }

      try {
        const existingSource = map.current.getSource('map-click-marker')
        if (existingSource) {
          // Update existing source
          const geoJsonSource = existingSource as mapboxgl.GeoJSONSource
          geoJsonSource.setData(geoJsonData)
          
          // Ensure marker stays on top when updating
          const allLayers = [
            'waypoint-markers', 
            'waypoint-cluster-labels',
            'poi-markers', 
            'search-result-marker', 
            'route-lines',
            'itinerary-routes-lines',
            'selected-waypoint-marker',
            'dead-end-markers',
            'route-buffer'
          ]
          allLayers.forEach(layerId => {
            if (map.current?.getLayer(layerId)) {
              try {
                map.current.moveLayer('map-click-marker', layerId)
              } catch (e) {
                // Layer might not exist, ignore
              }
            }
          })
          
          // Move to top
          try {
            if (map.current.getLayer('map-click-marker')) {
              map.current.moveLayer('map-click-marker')
            }
          } catch (e) {
            // Ignore if it fails
          }
        } else {
          // Create new source and layer
          map.current.addSource('map-click-marker', {
            type: 'geojson',
            data: geoJsonData
          })

          map.current.addLayer({
            id: 'map-click-marker',
            type: 'circle',
            source: 'map-click-marker',
            paint: {
              'circle-color': '#ef4444', // Red for clicked point
              'circle-radius': 12, // Increased size for better visibility
              'circle-stroke-color': '#ffffff',
              'circle-stroke-width': 4, // Increased stroke width
              'circle-opacity': 1.0 // Full opacity for better visibility
            }
          })

          // Ensure map click marker is on top of all other layers
          // Move it above all possible layers
          const allLayers = [
            'waypoint-markers', 
            'waypoint-cluster-labels',
            'poi-markers', 
            'search-result-marker', 
            'route-lines',
            'itinerary-routes-lines',
            'selected-waypoint-marker',
            'dead-end-markers',
            'route-buffer'
          ]
          allLayers.forEach(layerId => {
            if (map.current?.getLayer(layerId)) {
              try {
                map.current.moveLayer('map-click-marker', layerId)
              } catch (e) {
                // Layer might not exist, ignore
              }
            }
          })
          
          // Also try moving to top (moves to top of all layers)
          try {
            if (map.current.getLayer('map-click-marker')) {
              map.current.moveLayer('map-click-marker')
            }
          } catch (e) {
            // Ignore if it fails
          }
        }
      } catch (error) {
      }
    }

    // Helper function to add marker when style is ready
    const addMarker = (): void => {
      if (!map.current || !mapClickCoordinates) return
      
      // If context says style is loaded, trust it and proceed
      // (map.current.isStyleLoaded() can be false due to timing issues)
      if (styleLoaded) {
        addMarkerInternal()
        return
      }
      
      // If styleLoaded is false, wait for it
      if (!map.current.isStyleLoaded()) {
        map.current.once('styledata', () => {
          if (map.current && mapClickCoordinates) {
            addMarkerInternal()
          }
        })
        return
      }
      
      addMarkerInternal()
    }
    
    // Call the helper function
    addMarker()
  }, [map, isLoaded, styleLoaded, mapClickCoordinates])
}
